import * as msal from '@azure/msal-browser';
import {
  MeetingSummariesService,
  MeetingSummaryJobsService,
  MeetingTodosService,
  MsGraphOAuthService,
  type CreateMeetingSummaryJobRequest,
  type CreateMeetingSummaryRequest,
  type CreateMeetingTodoRequest,
  type EntityReference,
  type EntityReferencesCreateRequest,
  type EntityReferencesMatchResponse,
  type MeetingSummary,
  type MeetingSummaryJob,
  type MeetingSummaryJobList,
  type MeetingSummaryJobSelectedMeeting,
  type MeetingSummaryJobStatus,
  type MeetingSummaryList,
  type MeetingTodoList,
  type UpdateMeetingSummaryRequest,
  type UpdateMeetingTodoRequest,
} from '@beeboard/common';
import * as MicrosoftGraph from '@microsoft/microsoft-graph-client';
import {
  BatchRequestContent,
  BatchRequestStep,
  BatchResponseContent,
} from '@microsoft/microsoft-graph-client';
import { DateTime } from 'luxon';
import { defineStore } from 'pinia';
import {
  checkAuthentication as msCheckAuth,
  createGraphClient,
  createMsalConfig,
  getRefreshTokenFromCache,
  login as msLogin,
} from '@/utils/helpers/msGraphHelpers';
import { getRedirectURI, getTenant } from '@/utils/helpers/urlManipulation';
import { VisibilityAwareInterval } from '@/utils/visibilityAwareInterval';

// ============================================
// MS Graph Configuration
// ============================================

const subdomain = getTenant();
const tenantId = import.meta.env[`VITE_${subdomain}_MSTRANSCRIPT_TENANT_ID`];
const clientId = import.meta.env[`VITE_${subdomain}_MSTRANSCRIPT_CLIENT_ID`];
const config = createMsalConfig(clientId, tenantId, getRedirectURI());
const msalInstance = new msal.PublicClientApplication(config);

// Required scopes for transcript access and chat messaging
const SCOPES = [
  'Calendars.Read',
  'OnlineMeetingTranscript.Read.All',
  'OnlineMeetings.Read',
  'ChatMessage.Send', // For sending messages to meeting chat
  'Chat.ReadWrite', // For accessing chat threads
  'offline_access', // Required for refresh token (backend processing)
];

// Scope for backend API - used for OBO token exchange
const BACKEND_API_SCOPE = `api://${clientId}/access_as_user`;

// Storage key for MS Graph OAuth tokens
const MS_GRAPH_OAUTH_STORAGE_KEY = 'ms_graph_oauth';

// ============================================
// Type Definitions
// ============================================

export interface Attendee {
  emailAddress: {
    name: string;
    address: string;
  };
  type: string;
}

export interface OnlineMeetingParticipant {
  upn: string;
  role:
    | 'presenter'
    | 'attendee'
    | 'coorganizer'
    | 'unknownFutureValue'
    | string;
  identity: {
    user?: {
      id: string;
      displayName?: string | null;
      tenantId: string;
      identityProvider?: string;
    };
  };
}

export interface OnlineMeeting {
  id: string;
  subject: string;
  startDateTime: string;
  endDateTime: string;
  joinWebUrl: string;
  attendees?: Attendee[];
  organizer?: {
    emailAddress?: {
      name?: string;
      address?: string;
    };
  };
  chatInfo?: {
    threadId?: string;
  };
  // Participants from online meeting API (includes roles like coorganizer)
  participants?: {
    organizer?: OnlineMeetingParticipant;
    attendees?: OnlineMeetingParticipant[];
  };
  hasTranscripts?: boolean;
  transcriptCheckStatus?:
    | 'pending'
    | 'checking'
    | 'checked'
    | 'error'
    | 'no-access';
  // Flag to indicate if current user can access this meeting (organizer or coorganizer)
  userCanAccess?: boolean;
  // Online Meeting ID from the transcript (used for API calls)
  onlineMeetingId?: string;
  // Transcript ID from the first matching transcript (used to match with saved summaries)
  transcriptId?: string;
}

export interface Transcript {
  id: string;
  meetingId: string;
  meetingOrganizer: {
    user: {
      displayName: string;
      userIdentityType: string;
    };
  };
  createdDateTime: string;
}

export interface TranscriptWithContent extends Transcript {
  content?: string;
}

interface CalendarEvent {
  id: string;
  subject: string;
  start?: {
    dateTime: string;
  };
  end?: {
    dateTime: string;
  };
  onlineMeeting?: {
    joinUrl: string;
    conferenceId?: string;
    chatInfo?: {
      threadId?: string;
    };
  };
  onlineMeetingUrl?: string;
  organizer?: {
    emailAddress?: {
      name?: string;
      address?: string;
    };
  };
  attendees?: Attendee[];
  type?: 'singleInstance' | 'occurrence' | 'exception' | 'seriesMaster';
  seriesMasterId?: string;
}

interface MsGraphOAuthData {
  refreshToken: string;
  userEmail: string;
  timestamp: number;
}

interface MeetingStoreState {
  // MS Graph Authentication (SPA / live access)
  isAuthenticated: boolean;
  userEmail: string | null;
  _graphClient: MicrosoftGraph.Client | null;

  // MS Graph OAuth (backend / OBO flow)
  msGraphOAuth: MsGraphOAuthData | null;

  // Meetings & Transcripts
  meetings: OnlineMeeting[];
  transcripts: Transcript[];
  selectedTranscript: TranscriptWithContent | null;

  // Jobs (background processing)
  jobs: MeetingSummaryJobList['items'];
  currentJob: MeetingSummaryJob | null;
  jobPollingInterval: VisibilityAwareInterval | null;

  // Summaries
  summaries: MeetingSummaryList['items'];
  summariesTotal: number;
  currentSummary: MeetingSummary | null;

  // Todos
  todos: MeetingTodoList['items'];
  todosTotal: number;

  // Entity References
  entityReferences: EntityReference[];

  // UI State
  isLoading: boolean;
  error: string | null;
}

// ============================================
// Helper Functions
// ============================================

/**
 * Load stored OAuth data from sessionStorage
 */
function loadStoredOAuthData(): MsGraphOAuthData | null {
  try {
    const stored = sessionStorage.getItem(MS_GRAPH_OAUTH_STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored) as MsGraphOAuthData;
      // Check if data is less than 24 hours old (refresh tokens typically last longer, but be conservative)
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours
      if (Date.now() - data.timestamp < maxAge) {
        return data;
      }
    }
  } catch {
    // Ignore parse errors
  }

  return null;
}

/**
 * Save OAuth data to sessionStorage
 */
function saveOAuthData(data: MsGraphOAuthData): void {
  sessionStorage.setItem(MS_GRAPH_OAUTH_STORAGE_KEY, JSON.stringify(data));
}

/**
 * Clear stored OAuth data
 */
function clearStoredOAuthData(): void {
  sessionStorage.removeItem(MS_GRAPH_OAUTH_STORAGE_KEY);
}

// ============================================
// Store Definition
// ============================================

export const useMeetingStore = defineStore('meetingIntegrationStore', {
  state: (): MeetingStoreState => ({
    // MS Graph Authentication (SPA)
    isAuthenticated: false,
    userEmail: null,
    _graphClient: null,

    // MS Graph OAuth (backend / OBO flow)
    msGraphOAuth: loadStoredOAuthData(),

    // Meetings & Transcripts
    meetings: [],
    transcripts: [],
    selectedTranscript: null,

    // Jobs
    jobs: [],
    currentJob: null,
    jobPollingInterval: null,

    // Summaries
    summaries: [],
    summariesTotal: 0,
    currentSummary: null,

    // Todos
    todos: [],
    todosTotal: 0,

    // Entity References
    entityReferences: [],

    // UI State
    isLoading: false,
    error: null,
  }),

  getters: {
    // Lazy singleton: create Graph client once and reuse
    graphClient(state): MicrosoftGraph.Client {
      if (!state._graphClient) {
        state._graphClient = createGraphClient(msalInstance, SCOPES);
      }

      return state._graphClient as MicrosoftGraph.Client;
    },

    // OAuth getters
    hasMsGraphAuth: (state) => state.msGraphOAuth !== null,

    msGraphUserEmail: (state) => state.msGraphOAuth?.userEmail || null,

    isJobRunning: (state) =>
      state.currentJob?.status === 'pending' ||
      state.currentJob?.status === 'running',

    jobProgress: (state) => {
      if (!state.currentJob?.progress) {
        return null;
      }
      const { processed_meetings, total_meetings } = state.currentJob.progress;

      return {
        processed: processed_meetings,
        total: total_meetings,
        percent:
          total_meetings > 0
            ? Math.round((processed_meetings / total_meetings) * 100)
            : 0,
      };
    },

    /**
     * Get meetings that have transcripts and can be selected for summarization
     */
    meetingsWithTranscripts: (state): OnlineMeeting[] => {
      return state.meetings.filter(
        (m) => m.hasTranscripts && m.transcriptCheckStatus === 'checked',
      );
    },
  },

  actions: {
    // ============================================
    // Utility / Retry Actions
    // ============================================

    /**
     * Retry a function with exponential backoff for rate limiting (429 errors)
     */
    async retryWithBackoff<T>(
      fn: () => Promise<T>,
      maxRetries = 3,
      initialDelayMs = 1000,
    ): Promise<T> {
      let lastError: Error | null = null;

      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          return await fn();
        } catch (error) {
          lastError = error instanceof Error ? error : new Error(String(error));

          // Check if it's a throttling error (429)
          const isThrottling =
            lastError.message.includes('429') ||
            lastError.message.includes('throttle') ||
            lastError.message.includes('Too Many Requests');

          if (!isThrottling || attempt === maxRetries) {
            // Not a throttling error or max retries reached, throw immediately
            throw error;
          }

          // Calculate delay with exponential backoff
          const delayMs = initialDelayMs * Math.pow(2, attempt);
          console.warn(
            `Rate limit hit (429), retrying in ${delayMs}ms (attempt ${attempt + 1}/${maxRetries})`,
          );

          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
      }

      throw lastError;
    },

    // ============================================
    // MS Graph Authentication Actions (SPA / Live)
    // ============================================

    async login() {
      try {
        await msLogin(msalInstance, SCOPES);
        this.isAuthenticated = true;
      } catch (error) {
        console.error('Login failed:', error);
        this.error = error instanceof Error ? error.message : 'Login failed';
        throw error;
      }
    },

    /**
     * Clear local MSAL cache without doing a full Microsoft logout.
     * This preserves SSO sessions for other apps.
     */
    async clearMsalCache() {
      try {
        await msalInstance.initialize();
        // Only clear the active account, don't do a full logout
        // This preserves the SSO session for other apps
        msalInstance.setActiveAccount(null);

        // Clear session storage entries for this MSAL instance only
        const cacheKeys = Object.keys(sessionStorage).filter(
          (key) =>
            key.includes('msal') || key.includes('login.microsoftonline'),
        );
        cacheKeys.forEach((key) => sessionStorage.removeItem(key));

        this.isAuthenticated = false;
        this._graphClient = null;
        this.meetings = [];
        this.userEmail = '';
      } catch (error) {
        console.error('Clear MSAL cache failed:', error);
        // Force clear state even if cache clear fails
        this.isAuthenticated = false;
        this._graphClient = null;
        this.meetings = [];
        this.userEmail = '';
      }
    },

    async checkAuthentication() {
      try {
        const isAuth = await msCheckAuth(msalInstance, SCOPES);
        this.isAuthenticated = isAuth;

        return isAuth;
      } catch (error) {
        console.error('Authentication check failed:', error);
        this.isAuthenticated = false;

        return false;
      }
    },

    /**
     * Get an access token for the backend API scope (for OBO flow).
     * This token has the backend app as audience and can be exchanged via OBO for Graph tokens.
     * Returns null if not authenticated.
     */
    async getAccessToken(): Promise<string | null> {
      try {
        await msalInstance.initialize();

        const account =
          msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];

        if (!account) {
          return null;
        }

        // Request token for the backend API scope - this token can be exchanged via OBO
        const result = await msalInstance.acquireTokenSilent({
          scopes: [BACKEND_API_SCOPE],
          account,
        });

        return result.accessToken;
      } catch {
        // If silent fails, try interactive
        try {
          const result = await msalInstance.acquireTokenPopup({
            scopes: [BACKEND_API_SCOPE],
          });

          return result.accessToken;
        } catch {
          return null;
        }
      }
    },

    /**
     * Get the refresh token from MSAL cache for backend processing
     * Requires offline_access scope to be included in authentication
     * @returns The refresh token or null if not available
     */
    getRefreshToken(): string | null {
      return getRefreshTokenFromCache(clientId);
    },

    // ============================================
    // MS Graph OAuth Actions (Backend / OBO)
    // ============================================

    /**
     * Exchange the existing SPA MS Graph token for backend tokens using On-Behalf-Of flow.
     * This preserves the user's SSO session - no separate login or redirect required.
     *
     * @see https://learn.microsoft.com/en-us/entra/identity-platform/v2-oauth2-on-behalf-of-flow
     */
    async exchangeTokenForBackend() {
      try {
        // Ensure the user is authenticated with MS Graph (Teams Transcripts)
        if (!this.isAuthenticated) {
          throw new Error('Please log in to Microsoft Teams Transcripts first');
        }

        // Get the current SPA access token
        const spaAccessToken = await this.getAccessToken();

        if (!spaAccessToken) {
          throw new Error(
            'Could not get MS Graph access token. Please re-authenticate with Teams Transcripts.',
          );
        }

        // Exchange the SPA token for backend tokens via OBO flow
        const response = await MsGraphOAuthService.exchangeMsGraphToken({
          access_token: spaAccessToken,
        });

        // Store the OAuth data
        const oauthData: MsGraphOAuthData = {
          refreshToken: response.refresh_token,
          userEmail: response.user_email,
          timestamp: Date.now(),
        };
        this.msGraphOAuth = oauthData;
        saveOAuthData(oauthData);

        return true;
      } catch (error) {
        console.error('Failed to exchange token:', error);
        this.error =
          error instanceof Error
            ? error.message
            : 'Failed to exchange Microsoft token';
        throw error;
      }
    },

    /**
     * @deprecated Use exchangeTokenForBackend() instead - it uses OBO flow without redirects
     */
    async initiateOAuthFlow() {
      return this.exchangeTokenForBackend();
    },

    /**
     * Handle OAuth callback parameters from URL
     * Should be called when the page loads to check for OAuth callback data
     */
    handleOAuthCallback(): boolean {
      const urlParams = new URLSearchParams(window.location.search);

      // Check for OAuth error
      const oauthError = urlParams.get('oauth_error');
      if (oauthError) {
        const errorDescription =
          urlParams.get('error_description') || 'OAuth authentication failed';
        this.error = `Microsoft authentication failed: ${errorDescription}`;
        // Clean URL
        this.cleanOAuthParamsFromUrl();

        return false;
      }

      // Check for OAuth success
      const oauthSuccess = urlParams.get('oauth_success');
      if (oauthSuccess === 'true') {
        const msUserEmail = urlParams.get('ms_user_email');
        const msRefreshToken = urlParams.get('ms_refresh_token');

        if (msUserEmail && msRefreshToken) {
          // Store the OAuth data
          const oauthData: MsGraphOAuthData = {
            refreshToken: msRefreshToken,
            userEmail: msUserEmail,
            timestamp: Date.now(),
          };
          this.msGraphOAuth = oauthData;
          saveOAuthData(oauthData);

          // Clean URL
          this.cleanOAuthParamsFromUrl();

          return true;
        }
      }

      return false;
    },

    /**
     * Clean OAuth parameters from the URL without page reload
     */
    cleanOAuthParamsFromUrl() {
      const url = new URL(window.location.href);
      url.searchParams.delete('oauth_success');
      url.searchParams.delete('oauth_error');
      url.searchParams.delete('error_description');
      url.searchParams.delete('ms_user_email');
      url.searchParams.delete('ms_refresh_token');
      window.history.replaceState({}, '', url.toString());
    },

    /**
     * Clear stored OAuth credentials
     */
    clearOAuthData() {
      this.msGraphOAuth = null;
      clearStoredOAuthData();
    },

    // ============================================
    // Meeting & Transcript Actions
    // ============================================

    async fetchMeetings() {
      this.isLoading = true;
      this.error = null;

      try {
        // Get current user's email for filtering organizer/coorganizer
        const me = await this.graphClient.api('/me').select('mail').get();
        this.userEmail = me.mail;

        // Fetch calendar events from the past 60 days
        // Note: We can't filter by isOnlineMeeting, so we fetch all and filter in code
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 60);
        const endDate = new Date();

        // Get user's timezone from browser
        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const response = await this.graphClient
          .api('/me/calendarView')
          .header('Prefer', `outlook.timezone="${userTimeZone}"`)
          .query({
            startDateTime: startDate.toISOString(),
            endDateTime: endDate.toISOString(),
          })
          .select(
            'id,subject,start,end,onlineMeeting,onlineMeetingUrl,isOnlineMeeting,organizer,attendees,type,seriesMasterId',
          )
          .orderby('start/dateTime desc')
          .top(200)
          .get();

        const events: CalendarEvent[] = response.value || [];

        // Graph returns event start/end as naive wall-clock strings in the
        // user's timezone (requested above via `Prefer: outlook.timezone`), with
        // no offset/Z. Convert them to true UTC ISO so the `meeting_date`
        // forwarded to the backend carries an offset and is stored as the
        // correct instant — matching the manual-meeting flow (WI #5589).
        const toUtcIso = (dateTime: string | undefined): string =>
          dateTime
            ? (DateTime.fromISO(dateTime, { zone: userTimeZone })
                .toUTC()
                .toISO() ?? '')
            : '';

        // Filter for online meetings (including meetings where user is attendee)
        // Note: We'll filter later based on actual transcript access permissions
        // Don't fetch chatInfo here - we'll fetch it when a meeting is selected
        this.meetings = events
          .filter((event) => {
            const hasOnlineMeeting =
              event.onlineMeeting?.joinUrl || event.onlineMeetingUrl;

            return hasOnlineMeeting;
          })
          .map((event) => ({
            id: event.id,
            subject: event.subject || 'Untitled Meeting',
            startDateTime: toUtcIso(event.start?.dateTime),
            endDateTime: toUtcIso(event.end?.dateTime),
            joinWebUrl:
              event.onlineMeeting?.joinUrl || event.onlineMeetingUrl || '',
            attendees: event.attendees || [],
            organizer: event.organizer,
            chatInfo: undefined, // Will be populated when meeting is selected
            hasTranscripts: undefined,
            transcriptCheckStatus: 'pending',
          }));

        // Start checking for transcript availability in the background
        this.checkTranscriptAvailability().catch((error) => {
          console.error('Failed to check transcript availability:', error);
        });
      } catch (error) {
        console.error('Failed to fetch meetings:', error);
        this.error =
          error instanceof Error ? error.message : 'Failed to fetch meetings';
        this.meetings = [];
      } finally {
        this.isLoading = false;
      }
    },

    async fetchTranscriptsForMeeting(calendarEventId: string) {
      this.isLoading = true;
      this.error = null;

      try {
        // First, get the calendar event to extract the join URL and meeting times
        const event = await this.graphClient
          .api(`/me/events/${calendarEventId}`)
          .select('onlineMeeting,onlineMeetingUrl,subject,start,end')
          .get();

        const joinUrl = event.onlineMeeting?.joinUrl || event.onlineMeetingUrl;

        if (!joinUrl) {
          throw new Error('No online meeting URL found for this event');
        }

        // The key insight: we need to use /me/onlineMeetings and filter by joinWebUrl
        // According to Microsoft docs, we can get onlineMeeting by filtering on joinWebUrl
        // IMPORTANT: chatInfo will be included in the response (no $select needed - API doesn't support it)
        const encodedJoinUrl = encodeURIComponent(joinUrl);
        const meetingResponse = await this.graphClient
          .api('/me/onlineMeetings')
          .header('Prefer', 'include-unknown-enum-members')
          .filter(`JoinWebUrl eq '${encodedJoinUrl}'`)
          .get();

        const meetings = meetingResponse.value || [];

        if (meetings.length === 0) {
          throw new Error(
            'Could not find online meeting. The meeting may have expired or been deleted.',
          );
        }

        const onlineMeeting = meetings[0];
        const onlineMeetingId = onlineMeeting.id;

        // Extract chatInfo and participants data and update the meeting in the store
        const meetingIndex = this.meetings.findIndex(
          (m) => m.id === calendarEventId,
        );
        if (meetingIndex !== -1) {
          if (onlineMeeting.chatInfo?.threadId) {
            this.meetings[meetingIndex].chatInfo = {
              threadId: onlineMeeting.chatInfo.threadId,
            };
          }
          // Store participants with roles (organizer, coorganizer, attendee)
          if (onlineMeeting.participants) {
            this.meetings[meetingIndex].participants =
              onlineMeeting.participants;
          }
        }

        // Now fetch ALL transcripts using the onlineMeeting ID
        const transcriptResponse = await this.graphClient
          .api(`/me/onlineMeetings/${onlineMeetingId}/transcripts`)
          .get();

        const allTranscripts = transcriptResponse.value || [];

        // Filter transcripts to only those that match THIS specific occurrence's timeframe
        // This is important for recurring meetings where multiple occurrences share the same joinWebUrl
        if (event.start?.dateTime) {
          const meetingStart = new Date(event.start.dateTime);

          // Get the date boundaries for the meeting day (start of day to end of day)
          const meetingDayStart = new Date(meetingStart);
          meetingDayStart.setHours(0, 0, 0, 0);

          const meetingDayEnd = new Date(meetingStart);
          meetingDayEnd.setHours(23, 59, 59, 999);

          this.transcripts = allTranscripts.filter((transcript: Transcript) => {
            const transcriptCreated = new Date(transcript.createdDateTime);

            // Transcript should be created on the same day as the meeting
            return (
              transcriptCreated >= meetingDayStart &&
              transcriptCreated <= meetingDayEnd
            );
          });
        } else {
          // Fallback: if we don't have meeting times, show all transcripts
          this.transcripts = allTranscripts;
        }
      } catch (error) {
        console.error('Failed to fetch transcripts:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';

        if (
          errorMessage.includes('404') ||
          errorMessage.includes('Not Found') ||
          errorMessage.includes('not found')
        ) {
          this.error =
            'No transcripts found. This could mean:\n' +
            '• Transcription was not enabled during the meeting\n' +
            '• The meeting has expired (meetings older than 60 days)\n' +
            '• Additional permissions may be required';
        } else if (
          errorMessage.includes('403') ||
          errorMessage.includes('Forbidden')
        ) {
          this.error =
            'Permission denied. Please ensure your administrator has granted the "OnlineMeetingTranscript.Read.All" permission.';
        } else {
          this.error = `Failed to fetch transcripts: ${errorMessage}`;
        }

        this.transcripts = [];
      } finally {
        this.isLoading = false;
      }
    },

    async fetchTranscriptContent(
      calendarEventId: string,
      transcriptId: string,
    ) {
      this.isLoading = true;
      this.error = null;

      try {
        // Get the calendar event to extract the join URL
        const event = await this.graphClient
          .api(`/me/events/${calendarEventId}`)
          .select('onlineMeeting,onlineMeetingUrl')
          .get();

        const joinUrl = event.onlineMeeting?.joinUrl || event.onlineMeetingUrl;

        if (!joinUrl) {
          throw new Error('No online meeting URL found');
        }

        // Get the onlineMeeting by filtering on joinWebUrl
        const meetingResponse = await this.graphClient
          .api('/me/onlineMeetings')
          .header('Prefer', 'include-unknown-enum-members')
          .filter(`JoinWebUrl eq '${joinUrl}'`)
          .get();

        const meetings = meetingResponse.value || [];

        if (meetings.length === 0) {
          throw new Error('Could not find online meeting');
        }

        const onlineMeetingId = meetings[0].id;

        // Fetch the transcript content (VTT format)
        // Need to specify Accept header as text/vtt for transcript content
        const response = await this.graphClient
          .api(
            `/me/onlineMeetings/${onlineMeetingId}/transcripts/${transcriptId}/content`,
          )
          .header('Accept', 'text/vtt')
          .get();

        // The response might be a ReadableStream, so we need to read it
        let content: string;

        // Check if it's a ReadableStream by checking for the getReader method
        if (response && typeof response.getReader === 'function') {
          const reader = response.getReader();
          const decoder = new TextDecoder();
          let result = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              break;
            }
            result += decoder.decode(value, { stream: true });
          }
          content = result;
        } else if (typeof response === 'string') {
          content = response;
        } else {
          // If it's an object with a text property or similar
          content = String(response);
        }

        const transcript = this.transcripts.find((t) => t.id === transcriptId);
        if (transcript) {
          this.selectedTranscript = {
            ...transcript,
            content: content,
          };
        }

        return content;
      } catch (error) {
        console.error('Failed to fetch transcript content:', error);
        this.error =
          error instanceof Error
            ? error.message
            : 'Failed to fetch transcript content';

        return null;
      } finally {
        this.isLoading = false;
      }
    },

    clearTranscripts() {
      this.transcripts = [];
      this.selectedTranscript = null;
    },

    clearError() {
      this.error = null;
    },

    async sendChatMessage(
      chatId: string,
      htmlContent: string,
    ): Promise<boolean> {
      try {
        await this.graphClient.api(`/chats/${chatId}/messages`).post({
          body: {
            contentType: 'html',
            content: htmlContent,
          },
        });

        return true;
      } catch (error) {
        console.error('Failed to send chat message:', error);
        this.error =
          error instanceof Error
            ? error.message
            : 'Failed to send chat message';
        throw error;
      }
    },

    async checkTranscriptAvailability() {
      // Filter meetings that need checking
      const meetingsToCheck = this.meetings.filter(
        (meeting) =>
          meeting.transcriptCheckStatus === 'pending' ||
          meeting.transcriptCheckStatus === 'error',
      );

      if (meetingsToCheck.length === 0) {
        return;
      }

      // Process in batches to avoid rate limiting
      // Using 12 requests per batch to stay well within limits
      const BATCH_SIZE = 12;
      const BATCH_DELAY_MS = 300; // 300ms delay between batches

      for (let i = 0; i < meetingsToCheck.length; i += BATCH_SIZE) {
        const batch = meetingsToCheck.slice(i, i + BATCH_SIZE);

        // Add delay between batches (except for the first batch)
        if (i > 0) {
          await new Promise((resolve) => setTimeout(resolve, BATCH_DELAY_MS));
        }

        await this.checkTranscriptAvailabilityBatch(batch);
      }
    },

    async checkTranscriptAvailabilityBatch(meetings: OnlineMeeting[]) {
      // Mark all as checking
      meetings.forEach((meeting) => {
        meeting.transcriptCheckStatus = 'checking';
      });

      try {
        // Create batch request to get online meeting IDs for all meetings
        const batchSteps: BatchRequestStep[] = meetings.map(
          (meeting, index) => {
            const encodedJoinUrl = encodeURIComponent(meeting.joinWebUrl);

            return {
              id: `meeting-${index}`,
              request: new Request(
                `/me/onlineMeetings?$filter=JoinWebUrl eq '${encodedJoinUrl}'`,
                {
                  method: 'GET',
                  headers: {
                    Prefer: 'include-unknown-enum-members',
                  },
                },
              ),
            };
          },
        );

        const batchRequestContent = new BatchRequestContent(batchSteps);
        const content = await batchRequestContent.getContent();

        // Execute batch request with retry logic
        const batchResponse = await this.retryWithBackoff(async () => {
          return await this.graphClient.api('/$batch').post(content);
        });

        const batchResponseContent = new BatchResponseContent(batchResponse);

        // Process each response and fetch transcripts
        const transcriptRequests: Array<{
          meeting: OnlineMeeting;
          onlineMeetingId: string;
        }> = [];

        for (let index = 0; index < meetings.length; index++) {
          const meeting = meetings[index];
          const response = batchResponseContent.getResponseById(
            `meeting-${index}`,
          );

          if (!response.ok) {
            meeting.hasTranscripts = false;
            meeting.userCanAccess = false;
            // Check if it's a permission/access error
            if (response.status === 403 || response.status === 404) {
              meeting.transcriptCheckStatus = 'no-access';
            } else {
              meeting.transcriptCheckStatus = 'error';
            }
            continue;
          }

          const responseData = await response.json();
          const onlineMeetings = responseData.value || [];

          if (onlineMeetings.length === 0) {
            meeting.hasTranscripts = false;
            meeting.transcriptCheckStatus = 'checked';
            meeting.userCanAccess = false;
            continue;
          }

          const onlineMeeting = onlineMeetings[0];

          // Check if current user is organizer or coorganizer
          const isOrganizer =
            onlineMeeting.participants?.organizer?.upn?.toLowerCase() ===
            this.userEmail?.toLowerCase();
          const isCoorganizer = onlineMeeting.participants?.attendees?.some(
            (attendee: OnlineMeetingParticipant) =>
              attendee.upn?.toLowerCase() === this.userEmail?.toLowerCase() &&
              attendee.role === 'coorganizer',
          );

          meeting.userCanAccess = isOrganizer || isCoorganizer || false;

          transcriptRequests.push({
            meeting,
            onlineMeetingId: onlineMeeting.id,
          });
        }

        // Now batch the transcript requests
        if (transcriptRequests.length > 0) {
          const transcriptBatchSteps: BatchRequestStep[] =
            transcriptRequests.map((item, index) => ({
              id: `transcript-${index}`,
              request: new Request(
                `/me/onlineMeetings/${item.onlineMeetingId}/transcripts`,
                {
                  method: 'GET',
                },
              ),
            }));

          const transcriptBatchContent = new BatchRequestContent(
            transcriptBatchSteps,
          );
          const transcriptContent = await transcriptBatchContent.getContent();

          // Execute transcript batch request with retry logic
          const transcriptBatchResponse = await this.retryWithBackoff(
            async () => {
              return await this.graphClient
                .api('/$batch')
                .post(transcriptContent);
            },
          );

          const transcriptBatchResponseContent = new BatchResponseContent(
            transcriptBatchResponse,
          );

          // Process transcript responses
          for (let index = 0; index < transcriptRequests.length; index++) {
            const { meeting } = transcriptRequests[index];
            const response = transcriptBatchResponseContent.getResponseById(
              `transcript-${index}`,
            );

            if (!response.ok) {
              meeting.hasTranscripts = false;
              // Check if it's a permission/access error
              if (response.status === 403 || response.status === 404) {
                meeting.transcriptCheckStatus = 'no-access';
              } else {
                meeting.transcriptCheckStatus = 'error';
              }
              continue;
            }

            const transcriptData = await response.json();
            const allTranscripts = transcriptData.value || [];

            // Filter transcripts for this specific occurrence
            const meetingStart = new Date(meeting.startDateTime);
            const meetingDayStart = new Date(meetingStart);
            meetingDayStart.setHours(0, 0, 0, 0);

            const meetingDayEnd = new Date(meetingStart);
            meetingDayEnd.setHours(23, 59, 59, 999);

            const matchingTranscripts = allTranscripts.filter(
              (transcript: Transcript) => {
                const transcriptCreated = new Date(transcript.createdDateTime);

                return (
                  transcriptCreated >= meetingDayStart &&
                  transcriptCreated <= meetingDayEnd
                );
              },
            );

            meeting.hasTranscripts = matchingTranscripts.length > 0;
            meeting.transcriptCheckStatus = 'checked';
            // Store IDs from the first matching transcript
            if (matchingTranscripts.length > 0) {
              meeting.onlineMeetingId = matchingTranscripts[0].meetingId;
              meeting.transcriptId = matchingTranscripts[0].id;
            }
          }
        }
      } catch (error) {
        console.error('Batch transcript check failed:', error);

        // Mark all meetings in this batch as error
        meetings.forEach((meeting) => {
          if (meeting.transcriptCheckStatus === 'checking') {
            meeting.hasTranscripts = false;
            meeting.transcriptCheckStatus = 'error';
          }
        });

        // Check if it's a throttling error
        if (
          error instanceof Error &&
          (error.message.includes('429') ||
            error.message.includes('throttle') ||
            error.message.includes('Too Many Requests'))
        ) {
          console.warn(
            'Rate limit hit, will retry failed meetings in next check',
          );
        }
      }
    },

    // ============================================
    // Job Actions (Background Processing)
    // ============================================

    /**
     * Create a new meeting summary job using stored OAuth credentials
     * @param selectedMeetings Meetings to process
     * @param config Optional job configuration
     */
    async createJob(
      selectedMeetings: MeetingSummaryJobSelectedMeeting[],
      config?: CreateMeetingSummaryJobRequest['config'],
    ) {
      if (!this.msGraphOAuth) {
        this.error =
          'Microsoft authentication required. Please authenticate first.';

        return null;
      }

      const { refreshToken, userEmail } = this.msGraphOAuth;
      this.isLoading = true;
      this.error = null;

      try {
        const request: CreateMeetingSummaryJobRequest = {
          ms_refresh_token: refreshToken,
          ms_user_email: userEmail,
          selected_meetings: selectedMeetings,
          config,
        };

        const job =
          await MeetingSummaryJobsService.createMeetingSummaryJob(request);
        this.currentJob = job;
        this.jobs.unshift(job);

        // Start polling for job status
        this.startJobPolling(job.id);

        return job;
      } catch (error) {
        console.error('Failed to create meeting summary job:', error);
        this.error =
          error instanceof Error ? error.message : 'Failed to create job';
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Fetch all jobs for the current user
     */
    async fetchJobs(status?: MeetingSummaryJobStatus) {
      this.isLoading = true;
      this.error = null;

      try {
        const response =
          await MeetingSummaryJobsService.getMeetingSummaryJobs(status);
        this.jobs = response.items;

        return response;
      } catch (error) {
        console.error('Failed to fetch jobs:', error);
        this.error =
          error instanceof Error ? error.message : 'Failed to fetch jobs';
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Fetch a specific job by ID
     */
    async fetchJob(jobId: string) {
      try {
        const job = await MeetingSummaryJobsService.getMeetingSummaryJob(jobId);
        this.currentJob = job;

        // Update in jobs list if exists
        const index = this.jobs.findIndex((j) => j.id === jobId);
        if (index !== -1) {
          this.jobs[index] = job;
        }

        return job;
      } catch (error) {
        console.error('Failed to fetch job:', error);
        throw error;
      }
    },

    /**
     * Cancel a pending or running job
     */
    async cancelJob(jobId: string) {
      try {
        await MeetingSummaryJobsService.cancelMeetingSummaryJob(jobId);
        this.stopJobPolling();

        // Update job status locally
        if (this.currentJob?.id === jobId) {
          this.currentJob = null;
        }

        const index = this.jobs.findIndex((j) => j.id === jobId);
        if (index !== -1) {
          this.jobs.splice(index, 1);
        }
      } catch (error) {
        console.error('Failed to cancel job:', error);
        throw error;
      }
    },

    /**
     * Start polling for job status updates
     */
    startJobPolling(jobId: string) {
      this.stopJobPolling(); // Clear any existing polling

      // Track last processed count to detect new completions
      // Start with -1 so the first poll always syncs summaries
      let lastProcessedCount = -1;

      this.jobPollingInterval = new VisibilityAwareInterval(async () => {
        try {
          const job = await this.fetchJob(jobId);
          const currentProcessed = job.progress?.processed_meetings || 0;

          // If more meetings have been processed (or first poll), refresh summaries
          if (currentProcessed > lastProcessedCount) {
            lastProcessedCount = currentProcessed;
            // Only fetch summaries if there are actually processed meetings
            if (currentProcessed > 0) {
              await this.fetchSummaries({
                sortBy: 'meeting_date',
                sortOrder: 'desc',
                limit: 50,
              });
            }
          }

          // Stop polling if job is complete
          if (
            job.status === 'completed' ||
            job.status === 'failed' ||
            job.status === 'cancelled'
          ) {
            this.stopJobPolling();
            this.currentJob = null; // Clear current job so pending items disappear

            // Final refresh of summaries and todos
            if (job.status === 'completed') {
              await this.fetchSummaries({
                sortBy: 'meeting_date',
                sortOrder: 'desc',
                limit: 50,
              });
              await this.fetchTodos();
            }
          }
        } catch (error) {
          console.error('Job polling error:', error);
          this.stopJobPolling();
        }
      }, 3000); // Poll every 3 seconds
      this.jobPollingInterval.start();
    },

    /**
     * Stop job status polling
     */
    stopJobPolling() {
      if (this.jobPollingInterval) {
        this.jobPollingInterval.stop();
        this.jobPollingInterval = null;
      }
    },

    // ============================================
    // Summary Actions
    // ============================================

    /**
     * Create a meeting summary directly (for frontend-generated summaries)
     */
    async createSummary(request: CreateMeetingSummaryRequest) {
      this.isLoading = true;
      this.error = null;

      try {
        const summary =
          await MeetingSummariesService.createMeetingSummary(request);
        this.currentSummary = summary;

        // Only add to personal summaries list if not stored in project context
        if (!request.project_id) {
          this.summaries.unshift({
            id: summary.id,
            meeting_subject: summary.meeting_subject,
            meeting_date: summary.meeting_date,
            transcript_duration_minutes: summary.transcript_duration_minutes,
            participants: summary.participants || [],
            todo_count: summary.todo_count || 0,
            created_at: summary.created_at,
          });
          this.summariesTotal += 1;
        }

        return summary;
      } catch (error) {
        console.error('Failed to create summary:', error);
        this.error =
          error instanceof Error ? error.message : 'Failed to create summary';
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Fetch meeting summaries with pagination and filters
     */
    async fetchSummaries(options?: {
      page?: number;
      limit?: number;
      sortBy?: 'meeting_date' | 'created_at' | 'meeting_subject';
      sortOrder?: 'asc' | 'desc';
      dateFrom?: string;
      dateTo?: string;
      search?: string;
    }) {
      this.isLoading = true;
      this.error = null;

      try {
        const response = await MeetingSummariesService.getMeetingSummaries(
          options?.page ?? 1,
          options?.limit ?? 20,
          options?.sortBy ?? 'meeting_date',
          options?.sortOrder ?? 'desc',
          options?.dateFrom,
          options?.dateTo,
          options?.search,
        );

        this.summaries = response.items;
        this.summariesTotal = response.total;

        return response;
      } catch (error) {
        console.error('Failed to fetch summaries:', error);
        this.error =
          error instanceof Error ? error.message : 'Failed to fetch summaries';
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Fetch a specific summary by ID
     */
    async fetchSummary(summaryId: string) {
      this.isLoading = true;
      this.error = null;

      try {
        const summary =
          await MeetingSummariesService.getMeetingSummary(summaryId);
        this.currentSummary = summary;

        return summary;
      } catch (error) {
        console.error('Failed to fetch summary:', error);
        this.error =
          error instanceof Error ? error.message : 'Failed to fetch summary';
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Delete a meeting summary
     */
    async deleteSummary(summaryId: string) {
      try {
        await MeetingSummariesService.deleteMeetingSummary(summaryId);

        // Remove from local state
        this.summaries = this.summaries.filter((s) => s.id !== summaryId);
        this.summariesTotal = Math.max(0, this.summariesTotal - 1);

        if (this.currentSummary?.id === summaryId) {
          this.currentSummary = null;
        }
      } catch (error) {
        console.error('Failed to delete summary:', error);
        throw error;
      }
    },

    /**
     * Update a meeting summary
     */
    async updateSummary(
      summaryId: string,
      update: UpdateMeetingSummaryRequest,
    ) {
      try {
        const summary = await MeetingSummariesService.updateMeetingSummary(
          summaryId,
          update,
        );

        // Update current summary if it's the one being edited
        if (this.currentSummary?.id === summaryId) {
          this.currentSummary = summary;
        }

        // Update in summaries list
        const index = this.summaries.findIndex((s) => s.id === summaryId);
        if (index !== -1) {
          this.summaries[index] = {
            ...this.summaries[index],
            meeting_subject: summary.meeting_subject,
            project_meeting_id: summary.project_meeting_id,
          };
        }

        return summary;
      } catch (error) {
        console.error('Failed to update summary:', error);
        throw error;
      }
    },

    /**
     * Connect a meeting summary to a Beeboard project meeting
     * This updates the existing summary to link it to a project meeting
     */
    async connectToProject(
      summaryId: string,
      projectId: string,
      deletePersonalSummary: boolean = false,
      meetingId?: string | null,
    ) {
      try {
        await MeetingSummariesService.connectMeetingSummaryToProjectMeeting(
          summaryId,
          {
            project_id: projectId,
            delete_personal_summary: deletePersonalSummary,
            project_meeting_id: meetingId || undefined,
          },
        );

        // If personal summary was deleted, remove it from local state
        if (deletePersonalSummary) {
          this.summaries = this.summaries.filter((s) => s.id !== summaryId);
          this.summariesTotal = Math.max(0, this.summariesTotal - 1);

          if (this.currentSummary?.id === summaryId) {
            this.currentSummary = null;
          }
        }
      } catch (error) {
        console.error('Failed to connect summary to project:', error);
        throw error;
      }
    },

    // ============================================
    // Todo Actions
    // ============================================

    /**
     * Fetch meeting todos with filters
     */
    async fetchTodos(options?: {
      summary_id?: string;
      page?: number;
      limit?: number;
    }) {
      this.isLoading = true;
      this.error = null;

      try {
        const response = await MeetingTodosService.getMeetingTodos(
          options?.summary_id,
          options?.page ?? 1,
          options?.limit ?? 50,
        );

        this.todos = response.items;
        this.todosTotal = response.total;

        return response;
      } catch (error) {
        console.error('Failed to fetch todos:', error);
        this.error =
          error instanceof Error ? error.message : 'Failed to fetch todos';
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Update a todo (assignee, due date)
     */
    async updateTodo(todoId: string, update: UpdateMeetingTodoRequest) {
      try {
        const todo = await MeetingTodosService.updateMeetingTodo(
          todoId,
          update,
        );

        // Update in local state
        const index = this.todos.findIndex((t) => t.id === todoId);
        if (index !== -1) {
          this.todos[index] = todo;
        }

        return todo;
      } catch (error) {
        console.error('Failed to update todo:', error);
        throw error;
      }
    },

    /**
     * Delete a todo
     */
    async deleteTodo(todoId: string) {
      try {
        await MeetingTodosService.deleteMeetingTodo(todoId);

        // Remove from local state
        this.todos = this.todos.filter((t) => t.id !== todoId);
        this.todosTotal = Math.max(0, this.todosTotal - 1);
      } catch (error) {
        console.error('Failed to delete todo:', error);
        throw error;
      }
    },

    /**
     * Create a new todo
     */
    async createTodo(request: CreateMeetingTodoRequest) {
      try {
        const todo = await MeetingTodosService.createMeetingTodo(request);

        // Add to local state
        this.todos.unshift(todo);
        this.todosTotal += 1;

        return todo;
      } catch (error) {
        console.error('Failed to create todo:', error);
        throw error;
      }
    },

    // ============================================
    // Entity Reference Actions
    // ============================================

    /**
     * Run AI entity matching for a meeting summary
     */
    async matchEntityReferences(
      summaryId: string,
    ): Promise<EntityReferencesMatchResponse> {
      return MeetingSummariesService.matchMeetingSummaryEntities(summaryId);
    },

    /**
     * Fetch saved entity references for a meeting summary
     */
    async fetchEntityReferences(summaryId: string): Promise<EntityReference[]> {
      const references =
        await MeetingSummariesService.getMeetingSummaryEntityReferences(
          summaryId,
        );
      this.entityReferences = references;

      return references;
    },

    /**
     * Save entity references for a meeting summary (full replace)
     */
    async saveEntityReferences(
      summaryId: string,
      body: EntityReferencesCreateRequest,
    ): Promise<EntityReference[]> {
      const references =
        await MeetingSummariesService.createMeetingSummaryEntityReferences(
          summaryId,
          body,
        );
      this.entityReferences = references;

      return references;
    },

    /**
     * Clear entity references (e.g. when switching meetings)
     */
    clearEntityReferences() {
      this.entityReferences = [];
    },

    /**
     * Delete a single entity reference and remove it from in-memory state
     */
    async deleteEntityReference(
      summaryId: string,
      referenceId: string,
    ): Promise<void> {
      await MeetingSummariesService.deleteMeetingSummaryEntityReference(
        summaryId,
        referenceId,
      );
      this.entityReferences = this.entityReferences.filter(
        (r) => r.id !== referenceId,
      );
    },

    // ============================================
    // Utility Actions
    // ============================================

    /**
     * Clear all state (e.g., on logout)
     */
    clearState() {
      this.stopJobPolling();
      this.$reset();
    },
  },
});

// ============================================
// Re-export types for backward compatibility
// ============================================
export type {
  Attendee as TeamsAttendee,
  OnlineMeeting as TeamsOnlineMeeting,
  OnlineMeetingParticipant as TeamsOnlineMeetingParticipant,
  Transcript as TeamsTranscript,
  TranscriptWithContent as TeamsTranscriptWithContent,
};
